import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { appVersion } from "utils/params";

import { TMAP } from "constants/links";
import * as mapImages from "assets/images/map/urls";

export const MapContext = createContext({});

const CONSTANTS = {
  SET_MAP: "SET_MAP",
  SET_CURRENT_POSITION: "SET_CURRENT_POSITION",
  SET_CURRENT_CENTER: "SET_CURRENT_CENTER",
  SET_MAP_BOUNDS: "SET_MAP_BOUNDS",
  SET_SHOW_HIDE_MARKERS: "SET_SHOW_HIDE_MARKERS",
  SET_SHOW_MARKER_ADDRESS: "SET_SHOW_MARKER_ADDRESS",
  SET_LINK_OTHERS: "SET_LINK_OTHERS",
  SET_ORDERING_NUMBER_COLOR: "SET_ORDERING_NUMBER_COLOR",
  SET_NEXT_DELIVERY_TARGET: "SET_NEXT_DELIVERY_TARGET",
};

const MAP_CAMERA_STORAGE_KEY = "mapCamera";

const readCurrentCenterFromStorage = () => {
  try {
    const stored = localStorage.getItem("currentCenter");
    if (stored == null) return true;
    return JSON.parse(stored);
  } catch (e) {
    return true;
  }
};

const readMapCameraFromStorage = () => {
  try {
    const stored = localStorage.getItem(MAP_CAMERA_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (
      parsed == null ||
      parsed.lat == null ||
      parsed.lng == null ||
      Number.isNaN(Number(parsed.lat)) ||
      Number.isNaN(Number(parsed.lng))
    ) {
      return null;
    }
    return {
      lat: Number(parsed.lat),
      lng: Number(parsed.lng),
      zoom: parsed.zoom != null ? Number(parsed.zoom) : 15,
    };
  } catch (e) {
    return null;
  }
};

const writeMapCameraToStorage = (camera) => {
  try {
    localStorage.setItem(MAP_CAMERA_STORAGE_KEY, JSON.stringify(camera));
  } catch (e) {
    // ignore quota / private mode
  }
};

const persistMapCamera = (mapInstance) => {
  if (!mapInstance || !window.naver || !window.naver.maps) return;
  const center = mapInstance.getCenter();
  if (!center) return;
  const lat = typeof center.lat === "function" ? center.lat() : center._lat;
  const lng = typeof center.lng === "function" ? center.lng() : center._lng;
  if (lat == null || lng == null) return;
  writeMapCameraToStorage({
    lat,
    lng,
    zoom: mapInstance.getZoom(),
  });
};

const INITIAL_STATE = {
  map: null,
  mapBounds: null,
  currentPosition: null,
  currentCenter: true,
  showHideMarkers: true,
  showMarkerAddress: "ALL",
  orderingNumberColor: "red",
  linkOthers: TMAP,
  nextDeliveryTarget: null,
};

const CURRENT_POSITION_INIT = "CURRENT_POSITION_INIT";

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.SET_MAP:
      return { ...state, map: action.map };
    case CONSTANTS.SET_MAP_BOUNDS:
      return { ...state, mapBounds: action.mapBounds };
    case CONSTANTS.SET_CURRENT_POSITION:
      return { ...state, currentPosition: action.currentPosition };
    case CONSTANTS.SET_CURRENT_CENTER:
      return { ...state, currentCenter: action.currentCenter };
    case CONSTANTS.SET_SHOW_HIDE_MARKERS:
      return { ...state, showHideMarkers: action.showHideMarkers };
    case CONSTANTS.SET_SHOW_MARKER_ADDRESS:
      return { ...state, showMarkerAddress: action.showMarkerAddress };
    case CONSTANTS.SET_ORDERING_NUMBER_COLOR:
      return { ...state, orderingNumberColor: action.orderingNumberColor };
    case CONSTANTS.SET_LINK_OTHERS:
      return { ...state, linkOthers: action.linkOthers };
    case CONSTANTS.SET_NEXT_DELIVERY_TARGET:
      return { ...state, nextDeliveryTarget: action.nextDeliveryTarget };
    default:
      return state;
  }
};

export const MapProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, null, () => ({
    ...INITIAL_STATE,
    currentCenter: readCurrentCenterFromStorage(),
  }));

  return (
    <MapContext.Provider value={{ state, dispatch }}>
      {children}
    </MapContext.Provider>
  );
};

export const useMapStore = () => {
  const { dispatch, state } = useContext(MapContext);

  const mapRef = useRef(null);
  const currentMarkerRef = useRef(null);
  const lastHeadingRef = useRef(null);
  const currentCenterRef = useRef(readCurrentCenterFromStorage());
  const currentCenterTimeRef = useRef(0);
  const nextDeliveryArrowRef = useRef(null);
  const nextDeliveryTargetRef = useRef(null);
  const hasInitialCenteredRef = useRef(false);
  const pendingPositionRef = useRef(null);
  const applyPositionFnRef = useRef(() => {});
  const currentPositionRef = useRef(state.currentPosition);
  currentPositionRef.current = state.currentPosition;

  // bearing 계산 (현재위치 → 다음 배송지)
  const calcBearing = (lat1, lon1, lat2, lon2) => {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const φ1 = toRad(lat1);
    const φ2 = toRad(lat2);
    const Δλ = toRad(lon2 - lon1);
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x =
      Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return (Math.atan2(y, x) * (180 / Math.PI) + 360) % 360;
  };

  useEffect(() => {
    getCurrentCenterOnStorage();
    getHideMarkersOnStorage();
    getMakerAddressOnStorage();
    getOrderingNumberColorOnStorage();
    getLinkOthersOnStorage();
  }, []);

  const getCurrentCenterOnStorage = useCallback(async () => {
    const _currentCenter = await localStorage.getItem("currentCenter");
    if (_currentCenter) {
      currentCenterRef.current = JSON.parse(_currentCenter);
      dispatch({
        type: CONSTANTS.SET_CURRENT_CENTER,
        currentCenter: JSON.parse(_currentCenter),
      });
    }
  }, [dispatch]);

  const getHideMarkersOnStorage = useCallback(async () => {
    const _showHideMarkers = await localStorage.getItem("showHideMarkers");
    if (_showHideMarkers) {
      dispatch({
        type: CONSTANTS.SET_SHOW_HIDE_MARKERS,
        showHideMarkers: JSON.parse(_showHideMarkers),
      });
    }
  }, [dispatch]);

  const getMakerAddressOnStorage = useCallback(async () => {
    const _showMarkerAddress = await localStorage.getItem("showMarkerAddress");
    if (_showMarkerAddress) {
      if (_showMarkerAddress === "true") {
        dispatch({
          type: CONSTANTS.SET_SHOW_MARKER_ADDRESS,
          showMarkerAddress: "ALL",
        });
      } else if (_showMarkerAddress === "false") {
        dispatch({
          type: CONSTANTS.SET_SHOW_MARKER_ADDRESS,
          showMarkerAddress: "NONE",
        });
      } else {
        dispatch({
          type: CONSTANTS.SET_SHOW_MARKER_ADDRESS,
          showMarkerAddress: _showMarkerAddress,
        });
      }
    }
  }, [dispatch]);

  const getOrderingNumberColorOnStorage = useCallback(async () => {
    const _orderingNumberColor = await localStorage.getItem(
      "orderingNumberColor",
    );
    if (_orderingNumberColor) {
      dispatch({
        type: CONSTANTS.SET_ORDERING_NUMBER_COLOR,
        orderingNumberColor: _orderingNumberColor,
      });
    }
  }, [dispatch]);

  const getLinkOthersOnStorage = useCallback(async () => {
    const _linkOthers = await localStorage.getItem("linkOthers");
    if (_linkOthers) {
      dispatch({
        type: CONSTANTS.SET_LINK_OTHERS,
        linkOthers: _linkOthers,
      });
    }
  }, [dispatch]);

  const drawMap = useCallback(
    ({ zoom }) => {
      if (!(window.naver && window.naver.maps)) {
        return;
      }
      if (!document.getElementById("map")) {
        return;
      }

      const savedCamera = readMapCameraFromStorage();
      const lastPosition = currentPositionRef.current;
      const mapOptions = {
        zoom: (savedCamera && savedCamera.zoom) || zoom || 15,
      };

      if (savedCamera) {
        mapOptions.center = new window.naver.maps.LatLng(
          savedCamera.lat,
          savedCamera.lng,
        );
        hasInitialCenteredRef.current = true;
        currentCenterTimeRef.current = Date.now();
      } else if (lastPosition) {
        mapOptions.center = new window.naver.maps.LatLng(
          lastPosition.latitude,
          lastPosition.longitude,
        );
        hasInitialCenteredRef.current = true;
        currentCenterTimeRef.current = Date.now();
      }

      mapRef.current = new window.naver.maps.Map("map", mapOptions);
      persistMapCamera(mapRef.current);
      window.naver.maps.Event.addListener(mapRef.current, "idle", () => {
        persistMapCamera(mapRef.current);
      });

      dispatch({ type: CONSTANTS.SET_MAP, map: mapRef.current });
      const pending = pendingPositionRef.current;
      if (pending) {
        pendingPositionRef.current = null;
        applyPositionFnRef.current(pending.position, pending.type);
      } else if (lastPosition) {
        applyPositionFnRef.current(lastPosition);
      }
    },
    [dispatch],
  );

  const getCurrentPosition = useCallback(
    (_currentCenter) => {
      if (state.currentPosition) {
        if (!state.currentCenter) {
          setCurrentPostionMarker(state.currentPosition, CURRENT_POSITION_INIT);
        }
      } else {
        if (window.ReactNativeWebView) {
          if (appVersion) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: "currentPosition" }),
            );
          } else {
            window.ReactNativeWebView.postMessage("currentPosition");
          }
        }
      }

      localStorage.setItem("currentCenter", _currentCenter);
      currentCenterRef.current = JSON.parse(_currentCenter);
      dispatch({
        type: CONSTANTS.SET_CURRENT_CENTER,
        currentCenter: JSON.parse(_currentCenter),
      });
    },
    [
      dispatch,
      state.currentCenter,
      state.currentPosition,
      currentCenterRef.current,
      appVersion,
    ],
  );

  const updateCurrentPostion = useCallback(
    (position) => {
      if (
        !position ||
        position.latitude == null ||
        position.longitude == null
      ) {
        return;
      }
      if (currentMarkerRef.current) {
        setCurrentPostionMarker(position);
      } else {
        setCurrentPostionMarker(position, CURRENT_POSITION_INIT);
      }
    },
    [currentMarkerRef.current],
  );

  const showCurrentMaker = useCallback(() => {
    if (currentMarkerRef.current && mapRef.current) {
      currentMarkerRef.current.setMap(mapRef.current);
    }
  }, [currentMarkerRef.current]);

  const setNextDeliveryTarget = useCallback(
    (target) => {
      nextDeliveryTargetRef.current = target;
      dispatch({
        type: CONSTANTS.SET_NEXT_DELIVERY_TARGET,
        nextDeliveryTarget: target,
      });
    },
    [dispatch],
  );

  const setCurrentPostionMarker = useCallback(
    (position, type) => {
      if (!mapRef.current) {
        mapRef.current = state.map;
      }

      dispatch({
        type: CONSTANTS.SET_CURRENT_POSITION,
        currentPosition: position,
      });

      if (!(window.naver && window.naver.maps) || !mapRef.current) {
        pendingPositionRef.current = { position, type };
        return;
      }

      if (!hasInitialCenteredRef.current) {
        hasInitialCenteredRef.current = true;
        mapRef.current.setCenter(
          new window.naver.maps.LatLng(position.latitude, position.longitude),
        );
        mapRef.current.setOptions({ zoom: 18 });
        persistMapCamera(mapRef.current);
      } else if (
        currentCenterRef.current &&
        new Date().getTime() - currentCenterTimeRef.current > 5000
      ) {
        currentCenterTimeRef.current = new Date().getTime();
        mapRef.current.setCenter(
          new window.naver.maps.LatLng(position.latitude, position.longitude),
        );
      }

      if (currentCenterRef.current && type === CURRENT_POSITION_INIT) {
        mapRef.current.setOptions({ zoom: 18 });
      }

      const heading = position.heading || 0;
      const headingIcon = {
        content: [
          "<div>",
          `<img class="marker-current-heading" style="transform: rotate(${heading}deg); width: 20px;" src="${mapImages.markerCurrentHeading}" />`,
          "</div>",
        ].join(""),
        size: new window.naver.maps.Size(20, 20),
        scaledSize: new window.naver.maps.Size(20, 20),
      };

      if (currentMarkerRef.current) {
        currentMarkerRef.current.setPosition(
          new window.naver.maps.LatLng(position.latitude, position.longitude),
        );
        if (lastHeadingRef.current !== heading) {
          lastHeadingRef.current = heading;
          const markerEl =
            typeof currentMarkerRef.current.getElement === "function"
              ? currentMarkerRef.current.getElement()
              : null;
          const headingImg =
            markerEl && markerEl.querySelector
              ? markerEl.querySelector(".marker-current-heading")
              : null;
          if (headingImg) {
            headingImg.style.transform = `rotate(${heading}deg)`;
          } else {
            currentMarkerRef.current.setIcon(headingIcon);
          }
        }
      } else if (window.naver.maps) {
        lastHeadingRef.current = heading;
        currentMarkerRef.current = new window.naver.maps.Marker({
          map: mapRef.current,
          position: new window.naver.maps.LatLng(
            position.latitude,
            position.longitude,
          ),
          title: "Current",
          icon: headingIcon,
          zIndex: 100,
        });
        showCurrentMaker();
      }

      // ✅ 다음 배송지 화살표 마커 업데이트
      const nextTarget = nextDeliveryTargetRef.current;

      if (
        appVersion &&
        Number(appVersion) >= 47 &&
        nextTarget &&
        mapRef.current
      ) {
        const bearing = calcBearing(
          position.latitude,
          position.longitude,
          nextTarget.latitude,
          nextTarget.longitude,
        );

        const arrowContent = `
          <div style="
            width: 36px;
            height: 36px;
            display: flex;
            align-items: center;
            justify-content: center;
            transform: rotate(${bearing - 90}deg);
            pointer-events: none;
          ">
            <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
              <polygon
                points="2,14 22,14 22,8 34,18 22,28 22,22 2,22"
                fill="#FF4444"
                stroke="#fff"
                stroke-width="1.5"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        `;

        if (nextDeliveryArrowRef.current) {
          nextDeliveryArrowRef.current.setPosition(
            new window.naver.maps.LatLng(position.latitude, position.longitude),
          );
          nextDeliveryArrowRef.current.setIcon({
            content: arrowContent,
            size: new window.naver.maps.Size(36, 36),
            scaledSize: new window.naver.maps.Size(36, 36),
            anchor: new window.naver.maps.Point(18, 4),
          });
        } else {
          nextDeliveryArrowRef.current = new window.naver.maps.Marker({
            map: mapRef.current,
            position: new window.naver.maps.LatLng(
              position.latitude,
              position.longitude,
            ),
            title: "NextDeliveryArrow",
            icon: {
              content: arrowContent,
              size: new window.naver.maps.Size(36, 36),
              scaledSize: new window.naver.maps.Size(36, 36),
              anchor: new window.naver.maps.Point(18, 4),
            },
            zIndex: 99, // 현재위치 마커(100) 바로 아래
          });
        }
      } else {
        // 배송순서 없으면 화살표 제거
        if (nextDeliveryArrowRef.current) {
          nextDeliveryArrowRef.current.setMap(null);
          nextDeliveryArrowRef.current = null;
        }
      }
    },
    [
      dispatch,
      appVersion,
      state.map,
      currentMarkerRef.current,
      currentCenterRef.current,
      currentCenterTimeRef.current,
      mapRef.current,
    ],
  );

  applyPositionFnRef.current = setCurrentPostionMarker;

  const setMapCenter = useCallback(
    ({ latitude, longitude }) => {
      if (!mapRef.current) {
        mapRef.current = state.map;
      }
      if (mapRef.current && window.naver && window.naver.maps) {
        mapRef.current.setCenter(
          new window.naver.maps.LatLng(latitude, longitude),
        );
      }
    },
    [state.map, mapRef.current],
  );

  const setMapCurrentCenter = useCallback(() => {
    if (!mapRef.current) {
      mapRef.current = state.map;
    }
    if (
      mapRef.current &&
      state.currentPosition &&
      window.naver &&
      window.naver.maps
    ) {
      mapRef.current.setCenter(
        new window.naver.maps.LatLng(
          state.currentPosition.latitude,
          state.currentPosition.longitude,
        ),
      );
    }
  }, [state.map, state.currentPosition, mapRef.current]);

  const setShowHideMarkers = useCallback(
    (showHideMarkers) => {
      localStorage.setItem("showHideMarkers", showHideMarkers);
      dispatch({ type: CONSTANTS.SET_SHOW_HIDE_MARKERS, showHideMarkers });
    },
    [dispatch],
  );

  const setShowMarkerAddress = useCallback(
    (showMarkerAddress) => {
      localStorage.setItem("showMarkerAddress", showMarkerAddress);
      dispatch({ type: CONSTANTS.SET_SHOW_MARKER_ADDRESS, showMarkerAddress });
    },
    [dispatch],
  );

  const setOrderingNumberColor = useCallback(
    (orderingNumberColor) => {
      localStorage.setItem("orderingNumberColor", orderingNumberColor);
      dispatch({
        type: CONSTANTS.SET_ORDERING_NUMBER_COLOR,
        orderingNumberColor,
      });
    },
    [dispatch],
  );

  const setLinkOthers = useCallback(
    (linkOthers) => {
      localStorage.setItem("linkOthers", linkOthers);
      dispatch({ type: CONSTANTS.SET_LINK_OTHERS, linkOthers });
    },
    [dispatch],
  );

  return {
    state,
    drawMap,
    getCurrentPosition,
    updateCurrentPostion,
    setMapCenter,
    setMapCurrentCenter,
    setNextDeliveryTarget,
    setShowHideMarkers,
    setShowMarkerAddress,
    setOrderingNumberColor,
    setLinkOthers,
  };
};
